/**
 * OrgSettingsDialog - Organisation-level settings dialog
 *
 * A large non-fullscreen dialog (size="cover") for organisation-level configuration.
 * Displays the active organisation name and member count in the header,
 * with tab-based navigation gated by feature flags:
 * - "Members" tab: visible when `non_admin_org_management` is enabled
 * - "Data Connectors" tab: visible when `integration_management` is enabled
 *
 * Accessible from the Organisation Switcher dropdown for admin/owner users.
 */

import { useMemo } from "react"
import { Box, HStack, Spinner, Tabs, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@apollo/client"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { RouteProvider } from "@hydrologiq/shared/providers/RouteProvider"
import { useActiveOrganization, useAuth } from "../auth/AuthProvider"
import { useFeatureEnabled } from "../hooks/useFeatureToggles"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { DataConnectorsTab } from "@hydrologiq/shared/data-connectors/DataConnectorsTab"
import { MembersTab } from "../components/org-settings/MembersTab"

interface OrgSettingsDialogProps {
  open: boolean
  onClose: () => void
}

export function OrgSettingsDialog({ open, onClose }: OrgSettingsDialogProps) {
  const { activeOrganization } = useActiveOrganization()
  const { user } = useAuth()
  const orgManagementEnabled = useFeatureEnabled("non_admin_org_management")
  const integrationManagementEnabled = useFeatureEnabled("integration_management")

  const { data, loading } = useQuery(gqlTypes.GetOrganizationDetailsDocument, {
    variables: { organizationId: activeOrganization?.organization.id || "" },
    skip: !activeOrganization?.organization.id || !open,
    fetchPolicy: "cache-and-network",
  })

  const memberCount = data?.getOrganizationDetails?.members?.length ?? 0
  const orgName = activeOrganization?.organization.name ?? ""

  // Build available tabs based on feature flags
  const tabs = useMemo(() => {
    const available: Array<{ value: string; label: string }> = []
    if (orgManagementEnabled) {
      available.push({ value: "members", label: "Members" })
    }
    if (integrationManagementEnabled) {
      available.push({ value: "data-connectors", label: "Data Connectors" })
    }
    return available
  }, [orgManagementEnabled, integrationManagementEnabled])

  const defaultTab = tabs[0]?.value ?? ""

  return (
    <Dialog open={open} onClose={onClose} title="" size="cover">
      <RouteProvider route="">
        <VStack align="stretch" gap={6} width="100%">
          {/* Header: Organisation name and member count */}
          <Box>
            <HStack gap={3} align="baseline">
              <Text fontSize="2xl" fontWeight="bold" color="primary">
                {orgName}
              </Text>
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <Text fontSize="sm" color="fg.muted">
                  {memberCount} {memberCount === 1 ? "member" : "members"}
                </Text>
              )}
            </HStack>
          </Box>

          {/* Tab navigation */}
          {tabs.length > 0 && (
            <Tabs.Root defaultValue={defaultTab} variant={"sectionLozenge" as any}>
              <Tabs.List>
                {tabs.map((tab) => (
                  <Tabs.Trigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              {orgManagementEnabled && (
                <Tabs.Content value="members">
                  <MembersTab />
                </Tabs.Content>
              )}

              {integrationManagementEnabled && (
                <Tabs.Content value="data-connectors">
                  <DataConnectorsTab onNavigate={onClose} isPlatformAdmin={user?.role === "admin"} />
                </Tabs.Content>
              )}
            </Tabs.Root>
          )}
        </VStack>
      </RouteProvider>
    </Dialog>
  )
}
